/**
 * Persists which employee IDs have completed (or explicitly dismissed) the
 * one-time app-orientation tour. Keyed by employeeId so shared devices work
 * correctly: one employee completing the tour never hides it for another.
 *
 * This is a LOCAL guard only - it prevents re-showing the tour on the same
 * device without a network round-trip. The canonical completion record lives
 * in the Google Sheet (posted via `POST /api/progress/completions`) and is
 * recovered on boot via `hydrateStages`, which hydrates
 * `stages[APP_TOUR_SUB_MODULE_ID]` in the lesson-progress store - the
 * server-side check that survives a localStorage clear.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface AppTourState {
  /** employeeId → true when the tour was completed or skipped on this device. */
  seenBy: Record<string, boolean>
  markSeen: (employeeId: string) => void
}

export const useAppTour = create<AppTourState>()(
  persist(
    (set, get) => ({
      seenBy: {},
      markSeen: (employeeId) => {
        set({ seenBy: { ...get().seenBy, [employeeId]: true } })
      },
    }),
    { name: 'app-tour:v1' },
  ),
)

/** moduleId used when posting any tour completion event to the Sheet. */
export const APP_TOUR_MODULE_ID = '__system__'

/** subModuleId for the main dashboard orientation tour. */
export const APP_TOUR_SUB_MODULE_ID = '__app_tour__'

/** subModuleId for the accessibility-screen orientation tour. */
export const ACCESSIBILITY_TOUR_SUB_MODULE_ID = '__accessibility_tour__'
